import { AutoComplete, type Option } from "@/components/styled-autocomplete";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PaginationButton from "@/components/ui/pagination-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TableCell as CTableCell,
  TableHead as CTableHead,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { tableCellClass, tableCellLastRowClass, tableHeadClass } from "@/components/ui/table-styles";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { pharmacyClient } from "@/connect";
import { ItemType } from "@/gen/inventory/common_pb";
import { MedicalInventoryItem, NonMedicalInventoryItem } from "@/gen/onboarding/common_pb";
import { cn } from "@/lib/utils";
import { Route as StockTakingRoute } from "@/routes/(pharma)/_layout/stock-taking/index";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import getPharmacyID from "@/utils/pharmacy_code";
import {
  CaretSortIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  MixerHorizontalIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { SearchIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { transferMedicalItem, updateMedicalItem } from "../-data/api";
import { useStockTakingData } from "../-hooks/useStockTakingData";
import BarcodeModal from "../../inventory/add-stock/-components/BarcodeModal";

dayjs.extend(customParseFormat);

const TableCell = ({ className = "", ...props }) => (
  <CTableCell className={cn(tableCellClass, "py-0", className)} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <CTableHead className={cn(tableHeadClass, "", className)} {...props} />
);

// Define column type
interface Column {
  key: string;
  label: string;
}

const Component = () => {
  const searchParams = StockTakingRoute.useSearch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(searchParams.search);

  // Use the custom hook for data fetching
  const { data, isLoading, error, isFetching } = useStockTakingData();

  // Local loading state for search actions
  const [isSearching, setIsSearching] = useState(false);

  // Reset search loading when query completes
  useEffect(() => {
    if (!isFetching && isSearching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching]);

  // Helper function to update search params without full navigation
  const updateSearchParams = (updates: Partial<typeof searchParams>, showSearchLoading = false) => {
    if (showSearchLoading) {
      setIsSearching(true);
    }

    navigate({
      search: (prev: any) => ({ ...prev, ...updates }),
      replace: true, // This prevents creating new history entries
    });
  };

  // Sorting state
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("expiry_date");

  // Sort function
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle loading and error states
  if (error) {
    return <div className="p-8 text-center text-red-500">Error loading stock taking data</div>;
  }

  // Sorting data (handle loading state)
  const items = data?.items || [];
  const sortedData = items.slice().sort((a: any, b: any) => {
    if (orderBy === "expiry_date" || orderBy === "last_sold_date") {
      const getDateValue = (item: any, key: string) => {
        const value = item[key];
        if (!value) return 0;
        if (typeof value === "string") return new Date(value).getTime();
        if (value.Time) return new Date(value.Time).getTime();
        return 0;
      };
      const dateA = getDateValue(a, orderBy);
      const dateB = getDateValue(b, orderBy);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  const itemsPerPage = searchParams.pageSize || 10;
  const totalCount = data?.totalCount || 0;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / itemsPerPage) : 1;

  // Debug logging
  console.log('Pagination Debug:', {
    pageSize: searchParams.pageSize,
    itemsPerPage,
    dataLength: items.length,
    totalCount,
    totalPages,
    currentPage: searchParams.page
  });

  const columns: Column[] = [
    { key: "trade_name", label: "Trade Name" },
    { key: "available_stock", label: "Available Stock (Unit per stock)" },
    { key: "batch_number", label: "Batch Number" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "unit_type", label: "Unit Type" },
    { key: "barcode", label: "Barcode" },
    { key: "price", label: "Price" },
    { key: "cost", label: "Cost" },
    { key: "last_sold_date", label: "Last Sold On" },
    { key: "actions", label: "" },
  ];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>)
  );

  // Dialog state for NON_MEDICAL items
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true);

    // Barcode generation states - FIXED: Added showSearchResults and changed selectedBatch to selectedBatchIndex
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
    const [barcodeSearch, setBarcodeSearch] = useState("");
    const [barcodeSearchResults, setBarcodeSearchResults] = useState<any[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false); // NEW: Control dropdown visibility
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
    const [productBatches, setProductBatches] = useState<any[]>([]);
    const [selectedBatchIndex, setSelectedBatchIndex] = useState<string>("");
    const [customBarcodeGenerating, setCustomBarcodeGenerating] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState<any | null>(null);
    const [, setIsSelectingProduct] = useState(false);
    const [generatedBarcodeData, setGeneratedBarcodeData] = useState<{
    barcode: string;
    productName: string;
    batchNumber: string;
    expiryDate: any;
    unit: string;
    unitType: string;
    } | null>(null);
  
  // Form state for stock taking
  const [formData, setFormData] = useState({
    dataMatrixScan: "",
    batchNo: "",
    expiryDate: "",
    originalQuantity: "",
    cost: "",
    price: "",
    customBarcode: "",
    discountType: "NONE",
    discountValue: "",
    productionDate: "",
    serialNumber: "",
    barcode: "",
    unit: "",
    unitType: "",
  });

  // Autocomplete state for medical stock search
  const [autocompleteSearch, setAutocompleteSearch] = useState("");
  const [autocompleteResults, setAutocompleteResults] = useState<Option[]>([]);
  const [selectedMedicalItem, setSelectedMedicalItem] = useState<Option>();
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const debouncedAutocompleteSearch = useDebounce(autocompleteSearch, 300);

  const handleRowClick = (item: any) => {
    setSelectedItem(item);
    // Clear medical item selection when opening dialog
    setSelectedMedicalItem(undefined);
    setAutocompleteSearch("");
    setFormData({
      dataMatrixScan: "",
      batchNo: item.batch_number || "",
      expiryDate: item.expiry_date ? dayjs(item.expiry_date).format("YYYY-MM-DD") : "",
      originalQuantity: item.quantity?.original?.toString() || item.available_stock?.toString() || "",
      cost: item.cost?.toString() || "",
      price: item.price?.toString() || "",
      barcode: item.barcode || "",
      customBarcode: item.inventory_metadata?.custom_barcode || "",
      discountType: item.inventory_metadata?.discount_type || "NONE",
      discountValue: item.inventory_metadata?.discount_value?.toString() || "",
      productionDate: item.production_date?.Time ? dayjs(item.production_date.Time).format("YYYY-MM-DD") : "",
      serialNumber: "",
      unit: item.unit?.toString() || "",
      unitType: item.unit_type || "",
    });
    setIsDialogOpen(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only transfer for NON_MEDICAL items
    if (searchParams.type === "NON_MEDICAL" && selectedMedicalItem) {
      try {
        const response = await transferMedicalItem(
          selectedItem.nonMedicalMasterId,
          selectedMedicalItem.medical_master_id || '',
          formData.customBarcode || formData.barcode
        );
        
        if (response) {
          toast({ title: "Success", description: "Medical item transferred successfully" });
          setIsDialogOpen(false);
          resetForm();
        }
      } catch (error) {
        toast({ title: "Error", description: "Failed to transfer medical item", variant: "destructive" });
      }
    } else {
      // Handle stock taking form submission for medical items
      try {
        const response = await updateMedicalItem(
          selectedItem.medicalMasterId,
          {
            original: parseFloat(formData.originalQuantity) || 0,
            bonus: 0
          },
          formData.batchNo,
          formData.expiryDate,
          parseFloat(formData.cost),
          formData.productionDate || undefined
        );
        
        if (response) {
          toast({ title: "Success", description: "Medical item updated successfully" });
          setIsDialogOpen(false);
          resetForm();
        }
      } catch (error) {
        toast({ title: "Error", description: "Failed to update stock", variant: "destructive" });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      dataMatrixScan: "",
      batchNo: "",
      expiryDate: "",
      originalQuantity: "",
      cost: "",
      price: "",
      barcode: "",
      customBarcode: "",
      discountType: "NONE",
      discountValue: "",
      productionDate: "",
      serialNumber: "",
      unit: "",
      unitType: "",
    });
    setSelectedMedicalItem(undefined);
    setAutocompleteSearch("");
    setAutocompleteResults([]);
  };

  // useEffect for autocomplete medical stock search
  useEffect(() => {
    const fetchMedicalStock = async () => {
      if (!debouncedAutocompleteSearch || debouncedAutocompleteSearch.trim().length < 2) {
        setAutocompleteResults([]);
        return;
      }
  
      try {
        setIsAutocompleteLoading(true);
        const response = await pharmacyClient.getMedicalInventoryStock({
          pageNumber: 0,
          pageSize: 10,
          search: debouncedAutocompleteSearch,
        });
        

        const options: Option[] = response.items.map((item, index) => {

  
          return {
            value: `${item.medicalMasterId}-${item.barcode}-${index}`,
            label: `${item.tradeName} | ${item.barcode} | ${item.unitType}`,
            trade_name: item.tradeName,   
            barcode: item.barcode,
            unit: item.unit,                   
            unit_type: item.unitType,          
            available_stock: item.availableStock.toString(), 
            is_suggestion: "no",
            is_add: "no",
            ranking_score: "0",
            medical_master_id: item.medicalMasterId, 
          };
        });
        
        setAutocompleteResults(options);
      } catch (error) {
        console.error('Error fetching medical stock:', error);
        toast({
          title: "Error",
          variant: "destructive",
          description: "Failed to search medical stock",
          duration: 5000,
        });
        setAutocompleteResults([]);
      } finally {
        setIsAutocompleteLoading(false);
      }
    };

    fetchMedicalStock();
  }, [debouncedAutocompleteSearch]);

  const handleBatchSelect = (index: string) => {
    setSelectedBatchIndex(index);
    const batch = productBatches[parseInt(index)];
    setSelectedBatch(batch);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setBarcodeSearch("");
    setBarcodeSearchResults([]);
    setSelectedProduct(null);
    setProductBatches([]);
    setSelectedBatchIndex("");
    setSelectedBatch(null);
    setShowSearchResults(false);
    setGeneratedBarcodeData(null);
    setCustomBarcodeGenerating(false);
  };

  return (
    <>
      <Drawer
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) handleDrawerClose();
          setDrawerOpen(open);
        }}
        direction="right"
      >
        <DrawerContent className="focus-visible:outline-none w-fit min-w-[30vw] px-4">
          <DrawerClose asChild className="fixed inset-y-2 right-4">
            <Button className="h-fit aspect-square p-2 grid place-items-center bg-[#eceff8] hover:bg-[#eceff8]">
              <X className="text-[#667085] h-6 w-6" />
            </Button>
          </DrawerClose>

          {/* Drawer Content - Product Selection for Barcode */}
          <div className="w-full h-full overflow-auto py-6">
            <h2 className="text-lg font-semibold mb-4">Generate Barcode Sticker</h2>

            <form className="w-full flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
              {/* Product Search - FIXED: Added onFocus handler and improved state management */}
              <div className="pr-4 pb-4 border-b-[0.5px]">
                <p className="text-sm font-semibold mt-4 mb-2">
                  Product Name<span className="text-red-400">*</span>
                </p>
                <div className="flex-1 relative">
                  <div className="border rounded-md px-2 flex items-center gap-2">
                    <Input
                      placeholder="Search for a product"
                      className="text-center border-0"
                      value={barcodeSearch}
                      onChange={(e) => {
                        setBarcodeSearch(e.target.value);
                        if (!e.target.value) {
                          setSelectedProduct(null);
                          setProductBatches([]);
                          setSelectedBatchIndex("");
                        }
                      }}
                      onFocus={() => {
                        // NEW: Show results on focus if available
                        if (barcodeSearch.length >= 3 && barcodeSearchResults.length > 0) {
                          setShowSearchResults(true);
                        }
                      }}
                    />
                    <Button
                      className="px-2 py-0 h-8"
                      variant={"outline"}
                      size={"sm"}
                      type="button"
                      onClick={() => {
                        setBarcodeSearch("");
                        setBarcodeSearchResults([]);
                        setShowSearchResults(false);
                        setSelectedProduct(null);
                        setProductBatches([]);
                        setSelectedBatchIndex("");
                      }}
                    >
                      Clear
                    </Button>
                  </div>

                  {/* Search Results Dropdown - FIXED: Better positioning and visibility control */}
                  {showSearchResults && barcodeSearchResults.length > 0 && (
                    <Command className="w-full absolute top-full mt-1 z-50 border rounded-md shadow-md bg-white">
                      <CommandList>
                        {barcodeSearchResults.map((result, index: number) => (
                        <CommandItem
                          key={index}
                          onSelect={() => {
                            // Set flag to prevent search re-trigger
                            setIsSelectingProduct(true);
                            
                            // Clear previous selections
                            setSelectedBatchIndex("");
                            setSelectedBatch(null);
                            setGeneratedBarcodeData(null);
                            
                            // Set new product
                            setSelectedProduct({
                              id: result.masterId || result.id,
                              type: result.itemType || (result.type === ItemType.MEDICAL ? ItemType.MEDICAL : ItemType.NON_MEDICAL),
                              tradeName: result.tradeName,
                              unitType: result.unitType,
                              unit: result.unit,
                            });
                            
                            // Set search text (won't trigger search due to flag)
                            setBarcodeSearch(result.tradeName);
                            setShowSearchResults(false);
                          }}
                        >
                            {result.tradeName} | {result.unit} {result.unitType}
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  )}

                  {/* Show "No results" only when searching and no results */}
                  {barcodeSearch.length >= 3 && !showSearchResults && barcodeSearchResults.length === 0 && !selectedProduct && (
                    <Command className="w-full absolute top-full mt-1 z-50 border rounded-md shadow-md bg-white">
                      <CommandList>
                        <CommandEmpty>No results found</CommandEmpty>
                      </CommandList>
                    </Command>
                  )}
                </div>
              </div>

              {/* Batch Selection - FIXED: Use controlled Select with value */}
              {selectedProduct && productBatches.length > 0 && (
              <div className="pr-4">
                <p className="text-sm font-semibold mb-2">
                  Select Batch<span className="text-red-400">*</span>
                </p>
                <Select value={selectedBatchIndex} onValueChange={handleBatchSelect}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {productBatches.map((batch, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        Batch: {batch.batchNumber} - Exp: {batch.expiryDate ? dayjs(batch.expiryDate.toDate()).format("DD/MM/YYYY") : "N/A"} - Qty: {batch.availableQuantity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedProduct && selectedBatch && (
              <div className="pr-4 pt-4 border-t-[0.5px]">
                <Button 
                  size={"sm"} 
                  onClick={async () => {
                    try {
                      setCustomBarcodeGenerating(true);
                      
                      // Generate barcode using API
                      let generatedBarcode = "";
                      if (selectedBatch.itemType === ItemType.MEDICAL) {
                        const response = await pharmacyClient.generateCustomBarcode({
                          masterId: selectedBatch.masterId,
                          itemType: ItemType.MEDICAL
                        });
                        generatedBarcode = response.barcode as string;
                      } else {
                        const response = await pharmacyClient.generateCustomBarcode({
                          masterId: selectedBatch.masterId,
                          itemType: ItemType.NON_MEDICAL
                        });
                        generatedBarcode = response.barcode as string;
                      }
                      
                      // Store generated data
                      setGeneratedBarcodeData({
                        barcode: generatedBarcode,
                        productName: selectedBatch.tradeName,
                        batchNumber: selectedBatch.batchNumber,
                        expiryDate: selectedBatch.expiryDate,
                        unit: selectedBatch.unit,
                        unitType: selectedBatch.unitType
                      });
                      
                      // Open barcode modal
                      setBarcodeModalOpen(true);
                      
                    } catch (e) {
                      toast({
                        title: "Error",
                        variant: "destructive",
                        description: "Failed to generate barcode",
                        duration: 5000,
                      });
                      console.error(e);
                    } finally {
                      setCustomBarcodeGenerating(false);
                    }
                  }} 
                  disabled={customBarcodeGenerating}
                >
                  {customBarcodeGenerating ? "Please Wait..." : "Generate & Print Barcode"}
                </Button>
              </div>
            )}
            </form>
          </div>
        </DrawerContent>
        <div>
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => {
                      navigate({
                        to: "/inventory",
                      });
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    Inventory Overview
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Stock Taking</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="grid px-4 py-2 h-10">
              <Tabs defaultValue={searchParams.type}>
                <TabsList>
                  <TabsTrigger
                    value={"MEDICAL"}
                    onClick={() => {
                      updateSearchParams({
                        type: "MEDICAL",
                        page: 1,
                      });
                    }}
                  >
                    Medical
                  </TabsTrigger>
                  <TabsTrigger
                    value={"NON_MEDICAL"}
                    onClick={() => {
                      updateSearchParams({
                        type: "NON_MEDICAL",
                        page: 1,
                      });
                    }}
                  >
                    Audit Required
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* 
            Disabled Generate Barcode Button, uncomment when ready with functionality 
             <DrawerTrigger asChild>
              <Button size={"sm"}>Generate Barcode</Button>
            </DrawerTrigger>
            */}
            <DrawerTrigger asChild>
              <Button size={"sm"}>Generate Barcode</Button>
            </DrawerTrigger>
          </div>
          <hr className="my-4" />

          <div className="overflow-y-scroll h-screen pb-48">
            <div className="flex items-center justify-between mb-4">
              <div className="w-full flex items-center gap-2 justify-between">
                {/* Uncomment and configure as needed for search */}
                <div className="flex items-center gap-x-2">
                  <div className="relative w-72">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                      <SearchIcon className="w-4 h-4" />
                    </span>
                    <Input
                      placeholder="Search for item"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updateSearchParams({
                            search: searchValue,
                            page: 1,
                          }, true);
                        }
                      }}
                      className="pl-8 pr-8 w-full"
                    />
                    {searchValue && (
                      <Button
                        variant="ghost"
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 h-6 w-6 text-gray-400 hover:text-gray-700"
                        onClick={() => {
                          setSearchValue("");
                          updateSearchParams({
                            search: "",
                            page: 1,
                          });
                        }}
                      >
                        <span className="sr-only">Clear</span>
                        &#10005;
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    disabled={isSearching || isLoading}
                    onClick={() => {
                      updateSearchParams({
                        search: searchValue,
                        page: 1,
                      }, true);
                    }}
                  >
                    {isSearching || isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                        Searching...
                      </div>
                    ) : (
                      'Search'
                    )}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                      >
                        <MixerHorizontalIcon className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {columns.map((col) => (
                        <DropdownMenuCheckboxItem
                          key={col.key}
                          checked={visibleColumns[col.key]}
                          onCheckedChange={() =>
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [col.key]: !prev[col.key],
                            }))
                          }
                          disabled={col.key === "actions"} // Optionally always show actions
                        >
                          {col.label || "Actions"}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

            </div>

            <div className="mt-4 border rounded-lg bg-white overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-sm text-gray-500">Loading stock taking data...</div>
                </div>
              ) : !items || items.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-sm text-gray-500">No entries found</div>
                </div>
              ) : (
                <>
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow className="text-sm">
                        {columns.map(
                          (col) =>
                            visibleColumns[col.key] && (
                              <TableHead
                                key={col.key}
                                className={cn(
                                  col.key === "actions" ? "text-center" : "",
                                  col.key === "trade_name" ? "sticky left-0 bg-gray-50/50 border-r z-10" : ""
                                )}
                              >
                                {col.key === "expiry_date" || col.key === "last_sold_date" ? (
                                  <div
                                    className="cursor-pointer flex items-center gap-1"
                                    onClick={() => handleRequestSort(col.key)}
                                  >
                                    {col.label}
                                    <CaretSortIcon className="h-4 w-4" />
                                  </div>
                                ) : (
                                  col.label || "Actions"
                                )}
                              </TableHead>
                            )
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedData.map((item: MedicalInventoryItem | NonMedicalInventoryItem, index: number) => (
                        <TableRow
                          key={`${item.barcode}-${index}`}
                          className={cn(
                            "hover:bg-gray-50 cursor-pointer",
                            index === sortedData.length - 1 ? "" : "border-b"
                          )}
                          onClick={() => handleRowClick(item)}
                        >
                          {visibleColumns.trade_name && (
                            <TableCell
                              className={cn(
                                tableCellClass,
                                "font-medium sticky left-0 bg-white border-r z-10",
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.tradeName}
                            </TableCell>
                          )}
                          {visibleColumns.available_stock && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.availableStock} {item.unitType}
                            </TableCell>
                          )}
                          {visibleColumns.batch_number && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.batchNumber || "N/A"}
                            </TableCell>
                          )}
                          {visibleColumns.expiry_date && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : "",
                                item.expiryDate && dayjs(item.expiryDate).isBefore(dayjs()) ? "text-red-500" : ""
                              )}
                            >
                              {item.expiryDate ? dayjs(item.expiryDate).format("DD/MM/YYYY") : "N/A"}
                            </TableCell>
                          )}
                          {visibleColumns.unit_type && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.unitType}
                            </TableCell>
                          )}
                          {visibleColumns.barcode && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.barcode}
                            </TableCell>
                          )}
                          {visibleColumns.price && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {(item as any).price ? convertToCurrencyRounded((item as any).price) : "N/A"}
                            </TableCell>
                          )}
                          {visibleColumns.cost && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.cost ? convertToCurrencyRounded(item.cost) : "N/A"}
                            </TableCell>
                          )}
                          {visibleColumns.last_sold_date && (
                            <TableCell
                              className={cn(
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {item.lastSoldDate
                                ? dayjs(item.lastSoldDate).format("DD/MM/YYYY")
                                : "Never"}
                            </TableCell>
                          )}
                          {visibleColumns.actions && (
                            <TableCell
                              className={cn(
                                "text-center",
                                index === sortedData.length - 1 ? tableCellLastRowClass : ""
                              )}
                            >
                              {/* Add action buttons here if needed */}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between px-6 py-4 border-t">
                    <div className="text-sm text-gray-500">
                      Showing {((searchParams.page || 1) - 1) * itemsPerPage + 1} to{" "}
                      {Math.min((searchParams.page || 1) * itemsPerPage, totalCount)} of{" "}
                      {totalCount} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="hidden lg:flex">
                        <PaginationButton
                          onClick={() => updateSearchParams({ page: 1 })}
                          disabled={(searchParams.page || 1) <= 1}
                        >
                          <DoubleArrowLeftIcon className="h-4 w-4" />
                        </PaginationButton>
                      </div>
                      <PaginationButton
                        onClick={() => updateSearchParams({ page: (searchParams.page || 1) - 1 })}
                        disabled={(searchParams.page || 1) <= 1}
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </PaginationButton>
                      <span className="flex items-center gap-1 text-sm font-medium">
                        Page {searchParams.page || 1} of {totalPages}
                      </span>
                      <PaginationButton
                        onClick={() => updateSearchParams({ page: (searchParams.page || 1) + 1 })}
                        disabled={(searchParams.page || 1) >= totalPages}
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </PaginationButton>
                      <div className="hidden lg:flex">
                        <PaginationButton
                          onClick={() => updateSearchParams({ page: totalPages })}
                          disabled={(searchParams.page || 1) >= totalPages}
                        >
                          <DoubleArrowRightIcon className="h-4 w-4" />
                        </PaginationButton>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* Stock Taking Dialog for NON_MEDICAL items */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[70vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stock Taking - {selectedItem?.tradeName}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Item Details Section - Collapsible */}
            <div className="border rounded-lg">
              <button
                onClick={() => setIsDetailsCollapsed(!isDetailsCollapsed)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-semibold">{selectedItem?.tradeName}</h3>
                <ChevronDownIcon 
                  className={cn("h-5 w-5 transition-transform", !isDetailsCollapsed && "rotate-180")} 
                />
              </button>
              
              {!isDetailsCollapsed && (
                <div className="p-4 border-t bg-gray-50/50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-sm text-gray-700 mb-2">Product Information</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Trade Name:</span> {selectedItem?.tradeName}</div>
                        <div><span className="font-medium">Barcode:</span> {selectedItem?.barcode || "N/A"}</div>
                        <div><span className="font-medium">Unit Type:</span> {selectedItem?.unitType}</div>
                        <div><span className="font-medium">Available Stock:</span> {selectedItem?.availableStock}</div>
                        <div><span className="font-medium">VAT:</span> {selectedItem?.vat}%</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-sm text-gray-700 mb-2">Product Metadata</h4>
                      <div className="space-y-2 text-sm">
                        {searchParams.type === "NON_MEDICAL" ? (
                          <>
                            <div><span className="font-medium">Brand:</span> {selectedItem?.product_metadata?.brand || selectedItem?.master_metadata?.brand || "N/A"}</div>
                            <div><span className="font-medium">Manufacturer:</span> {selectedItem?.product_metadata?.manufacturer || selectedItem?.master_metadata?.manufacturer || "N/A"}</div>
                            <div><span className="font-medium">Owner:</span> {selectedItem?.product_metadata?.owner || selectedItem?.master_metadata?.owner || "N/A"}</div>
                            <div><span className="font-medium">Description:</span> {selectedItem?.product_metadata?.description || selectedItem?.master_metadata?.description || "N/A"}</div>
                          </>
                        ) : (
                          <>
                            <div><span className="font-medium">Manufacturer:</span> {selectedItem?.medicine_metadata?.manufacturer || "N/A"}</div>
                            <div><span className="font-medium">Owner:</span> {selectedItem?.medicine_metadata?.owner || "N/A"}</div>
                            <div><span className="font-medium">Scientific Name:</span> {selectedItem?.medicine_metadata?.scientific_name || "N/A"}</div>
                            <div><span className="font-medium">Strength:</span> {selectedItem?.medicine_metadata?.strength || "N/A"}</div>
                            <div><span className="font-medium">Route of Administration:</span> {selectedItem?.medicine_metadata?.route_of_administration || "N/A"}</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stock Taking Form */}
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <h3 className="text-lg font-semibold">Stock Taking Form</h3>
              
              {/* Medical Item Search - Only for NON_MEDICAL items */}
              {searchParams.type === "NON_MEDICAL" && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-4">Medical Item Search</h4>
                  <div className="space-y-2">
                    <Label htmlFor="medicalItemSearch">Map to Medical Master Item</Label>
                    <div className='border rounded-md px-2 flex items-center min-h-[40px]'>
                      <AutoComplete
                        options={autocompleteResults}
                        emptyMessage="Please enter a search query to search for medical items."
                        placeholder="Search for medical items..."
                        value={selectedMedicalItem}
                        onValueChange={(option) => {
                          setSelectedMedicalItem(option);
                          setFormData(prev => ({
                            ...prev,
                            customBarcode: option.barcode || "",
                          }));
                        }}
                        isLoading={isAutocompleteLoading}
                        input={autocompleteSearch}
                        onInputChanged={setAutocompleteSearch}
                        shouldFilter={false}
                      />
                    </div>
                    {selectedMedicalItem && (
                      <div className="p-3 bg-blue-50 rounded-md text-sm">
                        <div className="font-medium">Selected: {selectedMedicalItem.tradeName}</div>
                        <div className="text-gray-600">
                          Available Stock: {selectedMedicalItem.available_stock} {selectedMedicalItem.unitType}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Scanning & Identification */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-4">Scanning & Identification</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dataMatrixScan">Data Matrix Scan</Label>
                    <Input
                      id="dataMatrixScan"
                      value={formData.dataMatrixScan}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataMatrixScan: e.target.value }))}
                      placeholder="Scan data matrix code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customBarcode">Custom Barcode</Label>
                    <Input
                      id="customBarcode"
                      value={formData.customBarcode}
                      onChange={(e) => setFormData(prev => ({ ...prev, customBarcode: e.target.value }))}
                      placeholder="Enter custom barcode"
                    />
                  </div>
                  <div>
                    <Label htmlFor="batchNo">Batch Number</Label>
                    <Input
                      id="batchNo"
                      required
                      value={formData.batchNo}
                      onChange={(e) => setFormData(prev => ({ ...prev, batchNo: e.target.value }))}
                      placeholder="Enter batch number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="serialNumber">Serial Number</Label>
                    <Input
                      id="serialNumber"
                      value={formData.serialNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                      placeholder="Enter serial number"
                    />
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-4">Date Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expiryDate">Expiry Date</Label>
                    <Input
                      id="expiryDate"
                      type="date"
                      required
                      value={formData.expiryDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="productionDate">Production Date</Label>
                    <Input
                      id="productionDate"
                      type="date"
                      value={formData.productionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, productionDate: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Quantity & Pricing */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-4">Quantity & Pricing</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="unit">Unit</Label>
                    <Input
                      id="unit"
                      type="number"
                      required
                      value={formData.unit}
                      onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                      placeholder="Enter unit"
                    />
                  </div>
                  <div>
                    <Label htmlFor="unitType">Unit Type</Label>
                    <Input
                      id="unitType"
                      required
                      value={formData.unitType}
                      onChange={(e) => setFormData(prev => ({ ...prev, unitType: e.target.value }))}
                      placeholder="Enter unit type (e.g., tablets, bottles)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="originalQuantity">Original Quantity</Label>
                    <Input
                      id="originalQuantity"
                      type="number"
                      required
                      value={formData.originalQuantity}
                      onChange={(e) => setFormData(prev => ({ ...prev, originalQuantity: e.target.value }))}
                      placeholder="Enter original quantity"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cost">Cost</Label>
                    <Input
                      id="cost"
                      type="number"
                      step="0.01"
                      required
                      value={formData.cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                      placeholder="Enter cost"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">Price</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      required    
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="Enter price"
                    />
                  </div>
                </div>
              </div>

              {/* Discount Information */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-4">Discount Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="discountType">Discount Type</Label>
                    <Select 
                      value={formData.discountType} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, discountType: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select discount type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">None</SelectItem>
                        <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                        <SelectItem value="VALUE">Value</SelectItem>
                        <SelectItem value="ALWAYS">Always</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="discountValue">Discount Value</Label>
                    <Input
                      id="discountValue"
                      type="number"
                      step="0.01"
                      value={formData.discountValue}
                      onChange={(e) => setFormData(prev => ({ ...prev, discountValue: e.target.value }))}
                      placeholder="Enter discount value"
                      disabled={formData.discountType === "NONE"}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {searchParams.type === "NON_MEDICAL" && selectedMedicalItem 
                    ? "Transfer to Medical Item" 
                    : "Save Stock Taking Data"}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
            {/* Barcode Modal - FIXED: Proper null checks with useMemo computed value */}
            {generatedBarcodeData && (
              <BarcodeModal
                open={barcodeModalOpen}
                onOpenChange={setBarcodeModalOpen}
                initialData={{
                  pharmacyName: getPharmacyID(),
                  productName: generatedBarcodeData.productName,
                  expiryDate: generatedBarcodeData.expiryDate 
                    ? dayjs(generatedBarcodeData.expiryDate.toDate()).format("YYYY-MM-DD")
                    : new Date().toISOString().split('T')[0],
                  barcodeNumber: generatedBarcodeData.barcode,
                }}
                onPrintComplete={() => {
                  toast({
                    title: "Success",
                    variant: "default",
                    description: "Barcode printed successfully!",
                    duration: 3000,
                  });
                  handleDrawerClose();
                }}
              />
            )}
    </>
  );
};

export default Component;
